import React, { useState } from 'react';
import axios from 'axios';

const BulkGiveawayUpload = ({ onClose, onSuccess }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('upload'); // upload, validate, results

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Please upload a CSV file');
        return;
      }
      setCsvFile(file);
      setError('');
      setValidationResults(null);
      setUploadResults(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/giveaways/bulk/template', {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'giveaway-bulk-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading template:', error);
      setError('Failed to download template');
    }
  };

  const handleValidate = async () => {
    if (!csvFile) {
      setError('Please select a CSV file');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const formData = new FormData();
      formData.append('csv', csvFile);

      const token = localStorage.getItem('token');
      const response = await axios.post('/api/giveaways/bulk/validate', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setValidationResults(response.data);
      if (response.data.valid) {
        setStep('validate');
      }
    } catch (error) {
      console.error('Error validating CSV:', error);
      setError(error.response?.data?.message || 'Failed to validate CSV');
      setValidationResults(error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!csvFile) {
      setError('Please select a CSV file');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const formData = new FormData();
      formData.append('csv', csvFile);

      const token = localStorage.getItem('token');
      const response = await axios.post('/api/giveaways/bulk/upload', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadResults(response.data);
      setStep('results');
      
      if (response.data.summary.successful > 0 && onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      setError(error.response?.data?.message || 'Failed to upload CSV');
      setUploadResults(error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h2 style={{ margin: 0 }}>📦 Bulk Upload Giveaways</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '2rem', cursor: 'pointer' }}>
            ×
          </button>
        </div>

        {error && (
          <div style={{
            background: 'rgba(244, 67, 54, 0.1)',
            border: '1px solid rgba(244, 67, 54, 0.5)',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px',
            color: '#f44336'
          }}>
            {error}
          </div>
        )}

        {step === 'upload' && (
          <>
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '25px',
              color: 'white'
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' }}>
                📋 How to Use Bulk Upload
              </div>
              <ol style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
                <li>Download the CSV template</li>
                <li>Fill in your giveaway data following the format</li>
                <li>Upload the completed CSV file</li>
                <li>Review and validate your data</li>
                <li>Confirm to create all giveaways</li>
              </ol>
              <div style={{ marginTop: '15px', fontSize: '0.9rem', opacity: 0.9 }}>
                ⚠️ This feature is only available to <strong>Trusted Giveaway Hosts</strong>
              </div>
            </div>

            <button
              onClick={handleDownloadTemplate}
              style={{
                width: '100%',
                padding: '15px',
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginBottom: '25px'
              }}
            >
              📥 Download CSV Template
            </button>

            <div style={{
              border: '2px dashed var(--border)',
              borderRadius: '12px',
              padding: '40px',
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '15px' }}>📄</div>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                style={{
                  display: 'inline-block',
                  padding: '12px 30px',
                  background: 'var(--primary)',
                  color: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Choose CSV File
              </label>
              {csvFile && (
                <div style={{ marginTop: '15px', color: 'var(--text-secondary)' }}>
                  Selected: <strong>{csvFile.name}</strong>
                </div>
              )}
            </div>

            <button
              onClick={handleValidate}
              disabled={!csvFile || loading}
              style={{
                width: '100%',
                padding: '15px',
                background: csvFile && !loading ? 'var(--primary)' : 'var(--border)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: csvFile && !loading ? 'pointer' : 'not-allowed',
                opacity: csvFile && !loading ? 1 : 0.5
              }}
            >
              {loading ? '🔄 Validating...' : '✓ Validate CSV'}
            </button>
          </>
        )}

        {step === 'validate' && validationResults && (
          <>
            <div style={{
              background: validationResults.valid 
                ? 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' 
                : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '25px',
              color: 'white'
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '10px' }}>
                {validationResults.valid ? '✅ Validation Passed!' : '⚠️ Validation Issues Found'}
              </div>
              <div style={{ fontSize: '1.1rem' }}>
                {validationResults.rowCount} giveaway(s) found in CSV
              </div>
            </div>

            {validationResults.errors.length > 0 && (
              <div style={{
                background: 'rgba(244, 67, 54, 0.1)',
                border: '1px solid rgba(244, 67, 54, 0.5)',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#f44336', marginBottom: '15px' }}>
                  ❌ Errors ({validationResults.errors.length})
                </div>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {validationResults.errors.map((err, index) => (
                    <div key={index} style={{ marginBottom: '10px', fontSize: '0.9rem' }}>
                      <strong>Row {err.row}, {err.field}:</strong> {err.message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {validationResults.warnings.length > 0 && (
              <div style={{
                background: 'rgba(255, 193, 7, 0.1)',
                border: '1px solid rgba(255, 193, 7, 0.5)',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#ffc107', marginBottom: '15px' }}>
                  ⚠️ Warnings ({validationResults.warnings.length})
                </div>
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {validationResults.warnings.map((warn, index) => (
                    <div key={index} style={{ marginBottom: '10px', fontSize: '0.9rem' }}>
                      <strong>Row {warn.row}:</strong> {warn.message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '15px' }}>
              <button
                onClick={() => {
                  setStep('upload');
                  setValidationResults(null);
                  setCsvFile(null);
                }}
                style={{
                  flex: 1,
                  padding: '15px',
                  background: 'var(--border)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'var(--text)',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                ← Back
              </button>
              <button
                onClick={handleUpload}
                disabled={!validationResults.valid || loading}
                style={{
                  flex: 2,
                  padding: '15px',
                  background: validationResults.valid && !loading ? 'var(--primary)' : 'var(--border)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: validationResults.valid && !loading ? 'pointer' : 'not-allowed',
                  opacity: validationResults.valid && !loading ? 1 : 0.5
                }}
              >
                {loading ? '🔄 Creating Giveaways...' : '🚀 Create Giveaways'}
              </button>
            </div>
          </>
        )}

        {step === 'results' && uploadResults && (
          <>
            <div style={{
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              padding: '25px',
              borderRadius: '12px',
              marginBottom: '30px',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🎉</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '10px' }}>
                Upload Complete!
              </div>
              <div style={{ fontSize: '1.1rem' }}>
                Successfully created {uploadResults.summary.successful} giveaway(s)
              </div>
            </div>

            <div style={{
              background: 'var(--card-bg)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '15px' }}>
                📊 Upload Summary
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', textAlign: 'center' }}>
                <div>
                  <div style={{ fontSize: '2rem', color: 'var(--primary)' }}>{uploadResults.summary.total}</div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Total</div>
                </div>
                <div>
                  <div style={{ fontSize: '2rem', color: '#43e97b' }}>{uploadResults.summary.successful}</div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Successful</div>
                </div>
                <div>
                  <div style={{ fontSize: '2rem', color: '#f5576c' }}>{uploadResults.summary.failed}</div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Failed</div>
                </div>
              </div>
            </div>

            {uploadResults.results.length > 0 && (
              <div style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '15px' }}>
                  ✅ Created Giveaways
                </div>
                {uploadResults.results.map((result, index) => (
                  <div key={index} style={{
                    padding: '12px',
                    background: 'rgba(67, 233, 123, 0.1)',
                    borderRadius: '8px',
                    marginBottom: '10px'
                  }}>
                    <div style={{ fontWeight: 'bold' }}>{result.title}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      ID: {result.giveawayId} • Status: {result.status}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {uploadResults.errors.length > 0 && (
              <div style={{
                background: 'rgba(244, 67, 54, 0.1)',
                border: '1px solid rgba(244, 67, 54, 0.5)',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#f44336', marginBottom: '15px' }}>
                  ❌ Failed Rows
                </div>
                {uploadResults.errors.map((err, index) => (
                  <div key={index} style={{ marginBottom: '10px', fontSize: '0.9rem' }}>
                    <strong>Row {err.row}:</strong> {err.error}
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={onClose}
              style={{
                width: '100%',
                padding: '15px',
                background: 'var(--primary)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BulkGiveawayUpload;
